import type { MutableRefObject } from 'react'

import { COVER_ZOOM } from '../../../Stage/layouts'

import { drawMimicDebugOverlay, isMimicDebugEnabled } from './mimicDebugOverlay'
import {
  getMimicQualityTier,
  reportMimicRender,
} from './mimicQualityController'

// Replicates a floor feed's reframing: zoom [0-100] where COVER_ZOOM covers
// the box, below it approaches contain and above it zooms in up to 4x;
// centerX/centerY [-1, 1] pan the video within the available overflow.
interface MimicCrop {
  zoom: number
  centerX: number
  centerY: number
}

interface MimicEntry {
  mainCanvas: OffscreenCanvas
  mimics: Set<HTMLCanvasElement>
  lastRender: DOMHighResTimeStamp
  lastPresentedFrames: number | null
  lastRenderCost: number
  attachment: number
  // [0,1) phase offset staggering this track's renders relative to others,
  // so multiple tracks don't cluster their fan-out cost into the same
  // display frame
  stagger: number
}

const mimicCanvas = new WeakMap<MediaStreamTrack, MimicEntry>()

// Back-reference from a mimic canvas to its entry, so the resize observer can
// repaint a mimic without going through a stream/video element
const mimicOwners = new WeakMap<HTMLCanvasElement, MimicEntry>()

// Per-mimic reframing, kept off React state so the render loop can read it
// without prop drilling into the draw call
const mimicCrops = new WeakMap<HTMLCanvasElement, MimicCrop>()

// Defaults replicating the plain fit modes
const COVER_CROP: MimicCrop = { zoom: COVER_ZOOM, centerX: 0, centerY: 0 }
const CONTAIN_CROP: MimicCrop = { zoom: 0, centerX: 0, centerY: 0 }

// Draws the main canvas' current frame onto a single mimic. When the track is
// muted the main canvas is fully transparent, so this doubles as a clear.
//
// Replicates the floor's TrackPositioner rendering: the video is contained in
// its box (object-fit: contain), scaled about the box center by the zoom
// geometry, then translated by the crop center within the pan limits. Here
// the box is the mimic canvas — its buffer matches the element's on-screen
// size (kept in sync by the ResizeObserver), so c.width/c.height stand in
// for the display size without forcing layout.
const drawMimic = (entry: MimicEntry, c: HTMLCanvasElement) => {
  const mimicCtx = c.getContext('2d')
  if (!mimicCtx) return
  const canvas = entry.mainCanvas
  mimicCtx.clearRect(0, 0, c.width, c.height)

  const crop = mimicCrops.get(c) ?? CONTAIN_CROP

  const boxAR = c.width / c.height
  const sourceAR = canvas.width / canvas.height
  // Same geometry as TrackPositioner:
  // how much wider than the video is the box
  const ratioComp = boxAR / sourceAR
  const isWide = ratioComp >= 1
  // how much wider or taller than the video is the box; the cover/contain ratio
  const absRatio = isWide ? ratioComp : 1 / ratioComp

  // [0-1] how much to zoom out the video. equivalent to [cover - contain]
  const zoomOut = crop.zoom < COVER_ZOOM ? 1 - crop.zoom / COVER_ZOOM : 0
  // [0-1] how much to zoom in the video. equivalent to [cover - 4x]
  const zoomIn =
    crop.zoom > COVER_ZOOM ? (crop.zoom - COVER_ZOOM) / (100 - COVER_ZOOM) : 0
  // how much to scale the video based on zoom values. [contain - cover - 4x]
  const scale = 1 + (1 - zoomOut) * (absRatio - 1) + zoomIn * (4 - absRatio)
  // how much we're scaling in relation to covering the box
  const scaleRatio = scale / absRatio
  // how much bigger than the box the video gets scaled in the long/short axis
  const lScale =
    absRatio +
    zoomOut * (1 - absRatio) +
    zoomIn * (scaleRatio / ratioComp - absRatio)
  const sScale = 1 + zoomOut * (ratioComp - 1) + zoomIn * (scaleRatio - 1)
  const xLimit = ((isWide ? sScale : lScale) - 1) / 2
  const yLimit = ((isWide ? lScale : sScale) - 1) / 2

  // contained video size as a fraction of the box, then scaled and panned
  // about the box center — the same result the floor's CSS transform produces
  const drawWidth = scale * Math.min(1, sourceAR / boxAR)
  const drawHeight = scale * Math.min(1, boxAR / sourceAR)
  const centerX = 0.5 + xLimit * crop.centerX
  const centerY = 0.5 + yLimit * crop.centerY

  // Full-source draw into the transformed destination rect: the canvas clips
  // the overflow, and because the destination shape is derived from the same
  // buffer that CSS stretches onto the element, any buffer/display mismatch
  // cancels out
  mimicCtx.drawImage(
    canvas,
    0,
    0,
    canvas.width,
    canvas.height,
    (centerX - drawWidth / 2) * c.width,
    (centerY - drawHeight / 2) * c.height,
    drawWidth * c.width,
    drawHeight * c.height
  )

  if (isMimicDebugEnabled()) drawMimicDebugOverlay(c, entry.lastRenderCost)
}

// Keeps each mimic's backing buffer sized to its on-screen size, without
// reading clientWidth/clientHeight in the render loop, which would force
// synchronous layout on every frame. Buffer = element size means
// c.width/c.height is the display size, which the crop math relies on.
let mimicResizeObserver: ResizeObserver | null = null
const getMimicResizeObserver = () => {
  mimicResizeObserver ??= new ResizeObserver((entries) => {
    entries.forEach((entry) => {
      const canvas = entry.target as HTMLCanvasElement
      const [size] = entry.contentBoxSize
      // Size the buffer in device pixels so mimics stay crisp on HiDPI
      // screens; the crop math is unaffected since it only relies on the
      // buffer keeping the element's aspect ratio
      const dpr = window.devicePixelRatio || 1
      const width = Math.max(1, Math.round(size.inlineSize * dpr))
      const height = Math.max(1, Math.round(size.blockSize * dpr))
      // Assigning width/height always clears a canvas, even with the same
      // value — skip when nothing changed (e.g. observe() re-firing after a
      // React re-render re-attaches the canvas)
      if (canvas.width === width && canvas.height === height) return
      canvas.width = width
      canvas.height = height
      // Repaint immediately from the main canvas' last frame instead of
      // leaving the mimic blank until the next throttled render
      const owner = mimicOwners.get(canvas)
      if (owner) drawMimic(owner, canvas)
    })
  })
  return mimicResizeObserver
}

const getMimics = (stream: MediaStreamTrack) => {
  const existingMimic = mimicCanvas.get(stream)
  if (existingMimic) return existingMimic
  const newMimic: MimicEntry = {
    mainCanvas: new OffscreenCanvas(256, 144),
    mimics: new Set<HTMLCanvasElement>(),
    lastRender: -Infinity,
    lastPresentedFrames: null,
    lastRenderCost: 0,
    attachment: 0,
    stagger: Math.random(),
  }
  mimicCanvas.set(stream, newMimic)
  return newMimic
}

const attachMimic = (
  stream: MediaStreamTrack,
  canvas: HTMLCanvasElement | null
) => {
  if (!canvas) return
  const mimics = getMimics(stream)
  mimics.mimics.add(canvas)
  mimicOwners.set(canvas, mimics)
  getMimicResizeObserver().observe(canvas)
}

const removeMimic = (
  stream: MediaStreamTrack,
  canvas: HTMLCanvasElement | null
) => {
  if (!canvas) return
  const mimics = getMimics(stream)
  mimics.mimics.delete(canvas)
  // Only tear down the observer/owner if this removal comes from the canvas'
  // current owner — a stale removal (e.g. for a stream the canvas was
  // previously attached to) must not unobserve a re-attached canvas
  if (mimicOwners.get(canvas) === mimics) {
    mimicOwners.delete(canvas)
    getMimicResizeObserver().unobserve(canvas)
  }
}

const renderMimics = (
  stream: MediaStreamTrack,
  videoRef: MutableRefObject<HTMLVideoElement | null>,
  force?: boolean
) => {
  const mimics = getMimics(stream)
  const canvas = mimics.mainCanvas
  const streamSettings = stream.getSettings()
  if (streamSettings.aspectRatio) {
    const width = Math.round(streamSettings.aspectRatio * canvas.height)
    // Same-value width/height assignments still clear a canvas — skip them
    // (this runs on every React re-render through the video ref callback)
    if (canvas.width !== width) canvas.width = width
  }
  const ctx = canvas.getContext('2d')
  const attachment = ++mimics.attachment

  const render = (now?: DOMHighResTimeStamp) => {
    if (videoRef.current && ctx) {
      // Apply the current quality tier's resolution to the shared main
      // canvas; lowering it reduces the decode-copy cost for every mimic
      const tierHeight = getMimicQualityTier().height
      if (canvas.height !== tierHeight) {
        const aspectRatio = canvas.width / canvas.height
        canvas.height = tierHeight
        canvas.width = Math.max(1, Math.round(aspectRatio * tierHeight))
      }
      if (!videoRef.current.muted) {
        ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height)
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height)
      }
      if (now) mimics.lastRender = now
      mimics.mimics.forEach((c) => {
        drawMimic(mimics, c)
      })
    }
  }

  const throttledRender = (
    now: DOMHighResTimeStamp,
    metadata: VideoFrameCallbackMetadata
  ) => {
    // presentedFrames advancing by more than 1 between callbacks means video
    // frames were presented that we never got a callback for, i.e. the main
    // thread is falling behind
    const droppedFrames =
      mimics.lastPresentedFrames === null
        ? 0
        : Math.max(0, metadata.presentedFrames - mimics.lastPresentedFrames - 1)
    mimics.lastPresentedFrames = metadata.presentedFrames

    // Throttle by aligning renders to absolute-time buckets of the tier
    // interval, offset by this track's stagger phase: at most one render per
    // bucket, and different tracks cross their bucket boundaries in
    // different display frames, spreading the fan-out cost instead of
    // clustering all tracks into the same frame
    const interval = 1000 / getMimicQualityTier().fps
    const phase = mimics.stagger * interval
    const bucket = Math.floor((now - phase) / interval)
    const lastBucket = Math.floor((mimics.lastRender - phase) / interval)
    if (bucket > lastBucket) {
      const renderStart = performance.now()
      render(now)
      mimics.lastRenderCost = performance.now() - renderStart
      reportMimicRender(now, droppedFrames, mimics.lastRenderCost)
    } else {
      reportMimicRender(now, droppedFrames)
    }
    if (attachment >= mimics.attachment) {
      videoRef.current?.requestVideoFrameCallback(throttledRender)
    }
  }

  if (force) {
    render()
  } else {
    videoRef.current?.requestVideoFrameCallback(throttledRender)
  }
}

// Updates a mimic's reframing and repaints it immediately from the main
// canvas' last frame, so crop changes don't wait for the next throttled render
const setMimicCrop = (
  canvas: HTMLCanvasElement | null,
  crop: MimicCrop | undefined
) => {
  if (!canvas) return
  if (crop) {
    mimicCrops.set(canvas, crop)
  } else {
    mimicCrops.delete(canvas)
  }
  const owner = mimicOwners.get(canvas)
  if (owner) drawMimic(owner, canvas)
}

export {
  mimicCanvas,
  mimicOwners,
  attachMimic,
  renderMimics,
  removeMimic,
  setMimicCrop,
  type MimicCrop,
  COVER_CROP,
  CONTAIN_CROP,
}
